// ==========================
// Version 8 — components/TimelineFeed.js (minted badge)
// - v7 + NEW: show "Minted" badge for memories with nft.latest.txHash
// ==========================
import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isMemoryMarkedMinted } from '../nft/status';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';
const ACCESSIBLE_GOLD = '#8A6D2E';
const BURGUNDY = '#5C1A2B';

export default function TimelineFeed({ memories = [], onEdit, onDelete }) {
  const items = [...memories].sort((a, b) =>
    String(b.date || '').localeCompare(String(a.date || ''))
  );

  const observers = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    observers.current.forEach((o) => o.disconnect?.());
    observers.current = [];

    if (typeof window === 'undefined') return;
    if (!('IntersectionObserver' in window)) return;

    const prefersReduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReduce) return;

    const els = Array.from(document.querySelectorAll('.timeline__item'));
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );

    els.forEach((el) => io.observe(el));
    observers.current.push(io);

    return () => {
      observers.current.forEach((o) => o.disconnect?.());
      observers.current = [];
    };
  }, [items.length]);

  if (!items.length) return null;

  return (
    <div className="timeline" role="list" aria-label="Memories timeline">
      <div className="timeline__spine" />
      {items.map((m, idx) => {
        const left = idx % 2 === 0;
        const image =
          Array.isArray(m.imageUrls) && m.imageUrls.length ? m.imageUrls[0] : null;
        const linkTo = m.firestoreId ? `/m/${m.firestoreId}` : '#';

        const isMinted = isMemoryMarkedMinted(m);
        const actionLockReason = 'Minted memories cannot be changed.';

        const titleText =
          String(m.title || m.date || (m.story ? String(m.story).slice(0, 48) : 'Memory')).trim();

        const openCard = () => {
          if (linkTo && linkTo !== '#') navigate(linkTo, { state: { memory: m } });
        };

        const onCardKeyDown = (e) => {
          if (e.defaultPrevented) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openCard();
          }
        };

        return (
          <article
            key={m.firestoreId || m.id || idx}
            className={`timeline__item ${left ? 'timeline__item--left' : 'timeline__item--right'}`}
            role="listitem"
          >
            <div className="timeline__dot" />
            <div
              style={styles.card}
              tabIndex={0}
              onKeyDown={onCardKeyDown}
              onClick={openCard}
              aria-label={`Open memory: ${titleText}`}
            >
              <header style={styles.header}>
                <div style={styles.when}>
                  <time style={styles.date}>{m.date || 'No date'}</time>
                  <span style={styles.proof} aria-hidden>
                    • location + timestamp = proof
                  </span>
                </div>
                <div style={styles.actions}>
                  <button
                    type="button"
                    style={{
                      ...styles.btnGhost,
                      ...(isMinted ? styles.btnDisabled : null),
                    }}
                    disabled={isMinted}
                    title={isMinted ? actionLockReason : 'Edit memory'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMinted) return;
                      onEdit?.(m);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.btnDanger,
                      ...(isMinted ? styles.btnDisabled : null),
                    }}
                    disabled={isMinted}
                    title={isMinted ? actionLockReason : 'Delete memory'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMinted) return;
                      onDelete?.(m);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </header>

              <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>
                <Link to={linkTo} state={{ memory: m }} style={styles.titleLink}>
                  {titleText}
                </Link>
              </h3>

              {image && (
                <img src={image} alt="" loading="lazy" style={styles.cover} />
              )}

              {m.story && <p style={styles.story}>{m.story}</p>}

              <footer style={styles.footer}>
                <Link to={linkTo} state={{ memory: m }} style={styles.viewLink}>
                  View ↗
                </Link>

                {isMinted && (
                  <span style={styles.badgeMinted} aria-label="NFT minted">
                    🧾 Minted
                  </span>
                )}

                {m.isPublic && <span style={styles.badgeGold}>🌍 Public</span>}
                {m.isMediaPublic && (
                  <span style={styles.badgeOutline}>🖼️ Media Public</span>
                )}
              </footer>
            </div>
          </article>
        );
      })}
    </div>
  );
}

const styles = {
  card: {
    background: IVORY,
    color: NAVY,
    borderRadius: 16,
    border: '1px solid rgba(201,164,76,0.35)',
    boxShadow: '0 8px 28px rgba(10,26,51,0.25)',
    padding: 12,
    cursor: 'pointer',
  },
  header: { display: 'flex', justifyContent: 'space-between', marginBottom: 8 },
  when: { display: 'flex', gap: 8, alignItems: 'center' },
  date: { fontFamily: 'serif', fontWeight: 700 },
  proof: { color: WARM_GRAY, fontSize: 12 },
  actions: { display: 'flex', gap: 6 },
  btnGhost: {
    border: `1px solid ${GOLD}`,
    background: 'transparent',
    color: ACCESSIBLE_GOLD,
    borderRadius: 10,
    padding: '6px 10px',
    fontWeight: 800,
  },
  btnDanger: {
    border: 'none',
    background: BURGUNDY,
    color: IVORY,
    borderRadius: 10,
    padding: '6px 10px',
    fontWeight: 800,
  },
  btnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  titleLink: { color: NAVY, textDecoration: 'none', fontWeight: 900 },
  cover: {
    width: '100%',
    height: 220,
    objectFit: 'cover',
    borderRadius: 12,
    marginTop: 6,
  },
  story: { margin: '8px 0 6px', lineHeight: 1.5 },
  footer: { display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' },
  viewLink: { color: ACCESSIBLE_GOLD, fontWeight: 800, textDecoration: 'none' },
  badgeGold: {
    background: GOLD,
    color: NAVY,
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 700,
  },
  badgeOutline: {
    background: 'transparent',
    color: ACCESSIBLE_GOLD,
    border: `1px solid ${GOLD}`,
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 700,
  },
  badgeMinted: {
    background: NAVY,
    color: IVORY,
    border: `1px solid ${GOLD}`,
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 800,
  },
};
// ==========================
// End Version 8 — components/TimelineFeed.js
// ==========================
